import React, { useEffect, useState } from 'react';

import { Send as SendIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import {
  DistributionResult,
  PermissionSlipFormSummary,
  permissionSlipApiService,
} from '../../../services/permissionSlipApiService';
import { FormStatus } from '../../../types/permissionSlips';

type DistributionType =
  | 'students'
  | 'grade_levels'
  | 'school'
  | 'homerooms';

const parseNumberList = (value: string): number[] =>
  value
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isInteger(item) && item > 0);

const parseStringList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export const DistributionDashboard: React.FC = () => {
  const [forms, setForms] = useState<PermissionSlipFormSummary[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | ''>('');
  const [distributionType, setDistributionType] =
    useState<DistributionType>('students');
  const [studentIds, setStudentIds] = useState('');
  const [gradeLevels, setGradeLevels] = useState<string[]>([]);
  const [schoolId, setSchoolId] = useState('');
  const [homeroomTeachers, setHomeroomTeachers] = useState('');
  const [autoPopulate, setAutoPopulate] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DistributionResult | null>(null);

  useEffect(() => {
    const loadForms = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await permissionSlipApiService.getForms({
          status: FormStatus.ACTIVE,
          limit: 100,
        });
        setForms(response.forms);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load forms');
      } finally {
        setLoading(false);
      }
    };

    void loadForms();
  }, []);

  const distribute = async (): Promise<DistributionResult> => {
    if (selectedFormId === '') {
      throw new Error('Please select a form');
    }

    switch (distributionType) {
      case 'students': {
        const ids = parseNumberList(studentIds);
        if (ids.length === 0) {
          throw new Error('Enter at least one valid student ID');
        }
        return permissionSlipApiService.distributeToStudents({
          form_id: selectedFormId,
          student_ids: ids,
          auto_populate: autoPopulate,
        });
      }
      case 'grade_levels':
        if (gradeLevels.length === 0) {
          throw new Error('Select at least one grade level');
        }
        return permissionSlipApiService.distributeToGradeLevels({
          form_id: selectedFormId,
          grade_levels: gradeLevels,
          auto_populate: autoPopulate,
        });
      case 'school': {
        const id = Number(schoolId);
        if (!Number.isInteger(id) || id <= 0) {
          throw new Error('Enter a valid school ID');
        }
        return permissionSlipApiService.distributeToSchool({
          form_id: selectedFormId,
          school_id: id,
          auto_populate: autoPopulate,
        });
      }
      case 'homerooms': {
        const teachers = parseStringList(homeroomTeachers);
        if (teachers.length === 0) {
          throw new Error('Enter at least one homeroom teacher');
        }
        return permissionSlipApiService.distributeToHomerooms({
          form_id: selectedFormId,
          homeroom_teachers: teachers,
          auto_populate: autoPopulate,
        });
      }
    }
  };

  const handleDistribute = async () => {
    try {
      setLoading(true);
      setError(null);
      setResult(null);
      setResult(await distribute());
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to distribute form'
      );
    } finally {
      setLoading(false);
    }
  };

  const gradeOptions = [
    'K',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    '11',
    '12',
  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Permission Slip Distribution
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {result && (
        <Alert severity={result.errors.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
          Created {result.created_submissions} of {result.total_students}{' '}
          submissions; {result.skipped_existing} already existed.
          {result.errors.length > 0 && ` ${result.errors.length} errors occurred.`}
        </Alert>
      )}

      <Card>
        <CardContent>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Permission Slip Form</InputLabel>
            <Select
              value={selectedFormId}
              label="Permission Slip Form"
              onChange={(event) =>
                setSelectedFormId(Number(event.target.value))
              }
            >
              {forms.map((form) => (
                <MenuItem key={form.id} value={form.id}>
                  {form.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel>Distribution Type</InputLabel>
            <Select
              value={distributionType}
              label="Distribution Type"
              onChange={(event) =>
                setDistributionType(event.target.value as DistributionType)
              }
            >
              <MenuItem value="students">Individual Students</MenuItem>
              <MenuItem value="grade_levels">Grade Levels</MenuItem>
              <MenuItem value="school">Entire School</MenuItem>
              <MenuItem value="homerooms">Homerooms</MenuItem>
            </Select>
          </FormControl>

          {distributionType === 'students' && (
            <TextField
              fullWidth
              label="Student IDs"
              value={studentIds}
              onChange={(event) => setStudentIds(event.target.value)}
              helperText="Enter numeric student IDs separated by commas"
              sx={{ mb: 3 }}
            />
          )}

          {distributionType === 'grade_levels' && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Grade Levels</InputLabel>
              <Select
                multiple
                value={gradeLevels}
                label="Grade Levels"
                onChange={(event) =>
                  setGradeLevels(event.target.value as string[])
                }
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((grade) => (
                      <Chip key={grade} label={grade} size="small" />
                    ))}
                  </Box>
                )}
              >
                {gradeOptions.map((grade) => (
                  <MenuItem key={grade} value={grade}>
                    {grade}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {distributionType === 'school' && (
            <TextField
              fullWidth
              label="School ID"
              type="number"
              value={schoolId}
              onChange={(event) => setSchoolId(event.target.value)}
              sx={{ mb: 3 }}
            />
          )}

          {distributionType === 'homerooms' && (
            <TextField
              fullWidth
              label="Homeroom Teachers"
              value={homeroomTeachers}
              onChange={(event) => setHomeroomTeachers(event.target.value)}
              helperText="Enter teacher names separated by commas"
              sx={{ mb: 3 }}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={autoPopulate}
                onChange={(event) => setAutoPopulate(event.target.checked)}
              />
            }
            label="Auto-populate student and parent fields"
          />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleDistribute}
              disabled={loading || selectedFormId === ''}
            >
              {loading ? 'Sending…' : 'Send Permission Slip'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
